import React, { useState } from 'react';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatDate } from '../../../../../utils/formatting';
import { isValidDate } from '../../../../../utils/validation';

/**
 * Компонент для настройки пользовательских праздников
 */
const CustomHolidaysSection = ({ 
  formData, 
  setFormData, 
  errors 
}) => {
  const [newHoliday, setNewHoliday] = useState('');
  const [holidayError, setHolidayError] = useState('');

  // Обработчик добавления нового праздника
  const addHoliday = () => {
    if (!newHoliday) {
      setHolidayError('Выберите дату праздника');
      return;
    }

    if (!isValidDate(newHoliday)) {
      setHolidayError('Некорректная дата');
      return;
    }

    // Проверка на дубликаты
    if (formData.customHolidays.includes(newHoliday)) {
      setHolidayError('Эта дата уже добавлена в список');
      return;
    }

    // Добавляем новый праздник и сортируем
    const updatedHolidays = [...formData.customHolidays, newHoliday].sort();

    setFormData({
      ...formData,
      customHolidays: updatedHolidays
    });

    // Сбрасываем поле ввода и ошибку
    setNewHoliday('');
    setHolidayError('');
  };

  // Обработчик удаления праздника
  const removeHoliday = (holiday) => {
    const updatedHolidays = formData.customHolidays.filter(h => h !== holiday);
    setFormData({
      ...formData,
      customHolidays: updatedHolidays
    });
  };

  return (
    <div className="mb-6 bg-orange-50 dark:bg-orange-900/20 p-5 rounded-lg border border-orange-200 dark:border-orange-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <CalendarOff className="w-5 h-5 mr-2 text-orange-500" />
        Пользовательские праздники
      </h3>

      <div className="flex flex-col space-y-4">
        {/* Форма добавления нового праздника */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-grow">
            <label htmlFor="newHoliday" className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
              Добавить дату праздника или выходного дня:
            </label>
            <input
              type="date"
              id="newHoliday"
              value={newHoliday}
              onChange={(e) => {
                setNewHoliday(e.target.value);
                setHolidayError('');
              }}
              className={`form-input w-full ${holidayError ? 'input-error' : ''}`}
            />
            {holidayError && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {holidayError}
              </p>
            )}
          </div>
          <div className="self-end">
            <Button
              type="button"
              onClick={addHoliday}
              icon={<Plus className="w-4 h-4" />}
              variant="secondary"
              className="h-10"
            >
              Добавить
            </Button>
          </div>
        </div>

        {/* Список добавленных праздников */}
        {formData.customHolidays.length > 0 ? (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Добавленные праздники ({formData.customHolidays.length}):
            </h4>
            <div className="max-h-56 overflow-y-auto pr-2 border border-orange-200 dark:border-orange-800 rounded-md">
              <ul className="divide-y divide-orange-200 dark:divide-orange-800">
                {formData.customHolidays.map((holiday) => (
                  <li key={holiday} className="flex justify-between items-center py-2 px-3">
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {formatDate(holiday, 'long')}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeHoliday(holiday)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30"
                      aria-label="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic py-2">
            Вы не добавили ни одного пользовательского праздника. Используйте форму выше, чтобы добавить важные для вас даты.
          </div>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 p-3 bg-orange-100 dark:bg-orange-900/30 rounded">
          <p>
            <strong>Примечание:</strong> Пользовательские праздники будут учитываться при расчете рабочих дней.
            Они не будут считаться рабочими днями, даже если выпадают на будний день.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomHolidaysSection;